import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, Cell, Grid, PersonBlock, Separator } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { Link, useParams } from '@fs/zion-router'
import { getReservationAuthorization } from '../../services/reservationsService'
import useNotifications from '../../hooks/useNotifications'
import { getPersonSummary } from '../../services/personService'
import { convertUTCString } from '../../util/DateUtil'

const labelCss = css`
  font-weight: bold;
`
const summaryDivCss = css`
  padding-bottom: 2em;
`
export default function ReservationAuthorizationInfo() {
  const { t } = useTranslation()
  const [owsAuthorization, setOwsAuthorization] = useState({})
  const { error } = useNotifications()
  const { authorizationId } = useParams()

  const getAuthorization = () => {
    if (authorizationId) {
      getReservationAuthorization(authorizationId)
        .then((authorizationResponse) => {
          let ascendingPath = []
          let descendingPath = []
          let ascending = true
          let commonAncestor
          // split in to ascending and descending paths
          authorizationResponse.data.relationshipPath?.path.forEach((pathItem) => {
            if (pathItem.commonAncestor) {
              ascending = false
              commonAncestor = pathItem
            } else if (ascending) {
              ascendingPath.push(pathItem)
            } else {
              descendingPath.push(pathItem)
            }
          })
          // reverse ascending path
          ascendingPath.reverse()

          // create generations
          const maxGenerations =
            ascendingPath.length > descendingPath.length ? ascendingPath.length : descendingPath.length

          if (ascendingPath.length < maxGenerations) {
            ascendingPath = ascendingPath.concat(Array(maxGenerations).fill(undefined)).slice(0, maxGenerations)
          }

          if (descendingPath.length < maxGenerations) {
            descendingPath = descendingPath.concat(Array(maxGenerations).fill(undefined)).slice(0, maxGenerations)
          }

          const generations = []
          for (let i = 0; i < maxGenerations; i++) {
            generations.push({
              ascending: ascendingPath[i],
              descending: descendingPath[i],
            })
          }
          setOwsAuthorization({ ...authorizationResponse.data, commonAncestor, generations })
        })
        .catch(() => {
          error(`error retrieving reservation authorization with ID ${authorizationId}`)
        })
    }
  }

  useEffect(() => {
    getAuthorization()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!owsAuthorization || Object.keys(owsAuthorization).length === 0) {
    return null
  }

  return (
    <>
      <div css={summaryDivCss}>
        <Button
          emphasis="medium"
          onClick={() => {
            window.print()
          }}
        >
          {t('action.print', 'Print')}
        </Button>
        <Separator />
        <Card>
          <div>
            <span css={labelCss}>{t(`ows.authorization.timeStamp`, 'Authorization Time:')}: </span>
            <span>{convertUTCString(owsAuthorization.authorizationTimestamp, true)}</span>
          </div>
          <div>
            <span css={labelCss}>{t(`ows.authorization.relationship`, 'Relationship:')}: </span>
            {owsAuthorization.relationshipDescription}
          </div>
          <div>
            <span css={labelCss}>{t(`ows.authorization.adminSubmitted`, 'Admin Submitted:')}: </span>
            {owsAuthorization.adminSubmitter ? t(`yes`, 'Yes') : t(`no`, 'No')}
          </div>
          <div>
            <span css={labelCss}>{t(`ows.authorization.hops`, 'Hops:')}: </span>
            {owsAuthorization.hopsFromCommonAncestor === 1000 && owsAuthorization.relationshipDescription === 'None'
              ? '-'
              : owsAuthorization.hopsFromCommonAncestor}
          </div>
        </Card>
      </div>
      <Grid guttersY="none" alignX="center">
        <span columns={4}> </span>
        <Cell columns={2}>
          {owsAuthorization.commonAncestor && <PathPerson pathItem={owsAuthorization.commonAncestor} />}
        </Cell>
        <Cell columns={2}>
          {owsAuthorization.commonAncestor?.commonCoParent && (
            <PathPerson pathItem={owsAuthorization.commonAncestor?.commonCoParent} />
          )}
        </Cell>
        <span columns={4}> </span>
        {owsAuthorization?.generations?.map((generation) => {
          const ascending = generation.ascending ? (
            <PathPerson key={generation.ascending.personId} pathItem={generation.ascending} />
          ) : (
            <>&nbsp;</>
          )
          const descending = generation.descending ? (
            <PathPerson key={generation.descending.personId} pathItem={generation.descending} />
          ) : (
            <>&nbsp;</>
          )
          return [<Cell columns={6}>{ascending}</Cell>, <Cell columns={6}>{descending}</Cell>]
        })}
      </Grid>
    </>
  )
}

const PathPerson = ({ pathItem, centered = true }) => {
  const [person, setPerson] = useState(pathItem)

  useEffect(() => {
    getPersonSummary(pathItem.personId).then((personSummary) => {
      setPerson({ ...pathItem, summary: personSummary })
    })
  }, [pathItem])
  if (person) {
    return (
      <PersonBlock
        size="sm"
        avatarProps={{
          sex: `${person.summary?.gender.toLowerCase()}`,
        }}
        details={
          <>
            {`${person.relationshipToPrevious} • `}
            <Link
              id={`Person Link - ${person.personId}`}
              to={`/person/${person.personId}/summary?forward=false`}
              target="_blank"
            >
              {person.personId}
            </Link>
          </>
        }
        gender={person.summary?.gender || 'unknown'}
        name={person.summary?.name || ''}
        personId={person.personId}
        avatarSize="smaller"
        centered={centered}
      />
    )
  }
  return null
}
